import {
  signUp as amplifySignUp,
  signIn as amplifySignIn,
  signOut as amplifySignOut,
  confirmSignUp as amplifyConfirmSignUp,
  fetchAuthSession,
  resetPassword as amplifyResetPassword,
  confirmResetPassword as amplifyConfirmResetPassword,
  signInWithRedirect,
  getCurrentUser,
} from 'aws-amplify/auth';
import { isNative as platformIsNative } from '../utils/platform';
import useAuthStore from '../store/useAuthStore';
import { accountApi } from './api';

const NATIVE_TOKENS_KEY = 'cooksmith_tokens';

async function nativeAppleSignIn() {
  const { registerPlugin } = await import('@capacitor/core');
  const AppleSignIn = registerPlugin('AppleSignIn');
  return AppleSignIn.authorize();
}

function setUserFromTokens(idToken) {
  const payload = JSON.parse(atob(idToken.split('.')[1]));
  useAuthStore.getState().setUser({
    userId: payload.sub,
    username: payload['cognito:username'] || payload.sub,
    email: payload.preferred_username || payload.email,
    token: idToken,
  });
}

export async function checkAuth() {
  try {
    const currentUser = await getCurrentUser();
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken?.toString();
    const idPayload = idToken ? JSON.parse(atob(idToken.split('.')[1])) : {};
    useAuthStore.getState().setUser({
      userId: currentUser.userId,
      username: currentUser.username,
      email: idPayload.email || idPayload.preferred_username || currentUser.signInDetails?.loginId || currentUser.username,
      token: idToken,
    });
  } catch {
    useAuthStore.getState().clearUser();
  }
}

export async function signUp(email, password, name) {
  return amplifySignUp({
    username: email,
    password,
    options: { userAttributes: { email, name } },
  });
}

export async function confirmSignUp(email, code) {
  return amplifyConfirmSignUp({ username: email, confirmationCode: code });
}

export async function signIn(email, password) {
  const result = await amplifySignIn({ username: email, password });
  if (result.isSignedIn) await checkAuth();
  return result;
}

export async function signOut() {
  await amplifySignOut();
  useAuthStore.getState().clearUser();
  if (platformIsNative()) {
    try { localStorage.removeItem(NATIVE_TOKENS_KEY); } catch { /* noop */ }
  }
}

export async function signInWithApple() {
  if (platformIsNative()) {
    let appleResponse;
    try {
      appleResponse = await nativeAppleSignIn();
    } catch (err) {
      throw new Error(err?.message || 'Apple Sign In was cancelled or failed');
    }
    if (!appleResponse?.identityToken) {
      throw new Error('No identity token received from Apple');
    }
    const apiEndpoint = process.env.REACT_APP_API_ENDPOINT;
    const res = await fetch(`${apiEndpoint}/auth/apple-native`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        identityToken: appleResponse.identityToken,
        email: appleResponse.email,
        fullName: {
          givenName: appleResponse.givenName,
          familyName: appleResponse.familyName,
        },
      }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ message: 'Sign in failed' }));
      throw new Error(err.message);
    }
    const tokens = await res.json();
    localStorage.setItem(NATIVE_TOKENS_KEY, JSON.stringify(tokens));
    setUserFromTokens(tokens.idToken);
    return;
  }
  await signInWithRedirect({ provider: { custom: 'SignInWithApple' } });
}

export async function deleteAccount() {
  await accountApi.delete();
  await amplifySignOut();
  useAuthStore.getState().clearUser();
  if (platformIsNative()) {
    try { localStorage.removeItem(NATIVE_TOKENS_KEY); } catch { /* noop */ }
  }
}

export async function forgotPassword(email) {
  return amplifyResetPassword({ username: email });
}

export async function confirmForgotPassword(email, code, newPassword) {
  return amplifyConfirmResetPassword({ username: email, confirmationCode: code, newPassword });
}

export async function getAuthToken() {
  if (platformIsNative()) {
    try {
      const stored = localStorage.getItem(NATIVE_TOKENS_KEY);
      if (stored) return JSON.parse(stored).idToken;
    } catch { /* fall through */ }
  }
  try {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString();
  } catch {
    return null;
  }
}
